import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "../../../../components/TextField";
import Button from "../../../../components/Button";
import GridTable from "../../../../components/GridTable";
import Dialog from "../../../../components/Dialog";
import Loader from "../../../../components/Loader";
import ActionMenu from "../../../../components/ActionMenu";
import Dropdown from "../../../../components/Dropdown";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { errorMessage, successMessage } from "../../../../components/Toaster";
import { getLocationsByCompanyId } from "../../location/api"
import { LocationData } from "../../location/interface"
import { getErrorMessage } from "../../../../utils/error";
import { getCompanyMachineInstancesByCompanyMachineID, getReadyMachineInstancesByMachineID, addCompanyMachineInstance, deleteCompanyMachineInstance, getAllWindowsAccessPasswords, getWindowsAccessPassword, createWindowsAccessPassword } from "./api";
import { ApiResponse, CompanyMachineInstance, CompanyMachineInstanceParams, GetWindowsAccessPasswordResponse, GetAllWindowsAccessPasswordData, CreateWindowsAccessPasswordParams } from "./interface";

const Instance: React.FC = () => {
    const location = useLocation();
    const { company_id, company_machine_id } = useParams<{
        company_id: string;
        company_machine_id: string;
    }>();
    const company_name = location.state?.company_name || "Company";
    const machine_name = location.state?.machine_name || "Machine";
    const machine_id = location.state?.machine_id || "";
    const [companyMachineInstances, setCompanyMachineInstances] = useState<CompanyMachineInstance[]>([]);
    const [allWindowsPasswords, setAllWindowsPasswords] = useState<GetAllWindowsAccessPasswordData[]>([]);
    const [windowsPasswords, setWindowsPasswords] = useState<Partial<GetWindowsAccessPasswordResponse>>({});
    const [instances, setInstances] = useState<String[]>([]);
    const [locations, setLocations] = useState<LocationData[]>([]);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isPasswordAttachDialogOpen, setPasswordAttachDialogOpen] = useState(false);
    const [isViewhDialogOpen, setIsViewDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [machineInstanceToAttach, setMachineInstanceToAttach] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        locationId: Yup.string().required("Location is required"),
        machineInstanceId: Yup.string().required("Machine Instance is required")
    });

    const validationSchemaForWindowsPassword = Yup.object().shape({
        adminPassword: Yup.string()
            .required("Admin password is required")
            .min(10, "Admin password must be at least 10 characters long")
            .matches(
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^()_\-+=])[A-Za-z\d@$!%*#?&^()_\-+=]{10,}$/,
                "Admin password must include letters, numbers, and symbols"
            ),

        serviceEnggPassword: Yup.string()
            .required("Service Engineer password is required")
            .matches(/^\d{6}$/, "Service Engineer password must be exactly 6 digits"),

        userPassword: Yup.string()
            .required("User password is required")
            .matches(/^\d{4}$/, "User password must be exactly 4 digits"),
    });

    const columns: GridColDef[] = [
        { field: "machine_instance_id", headerName: "Instance ID", flex: 1 },
        { field: "location_name", headerName: "Location Name", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <LockResetIcon color="primary" />,
                            tooltip: "Password",
                            onClick: () => handleAttachPassword(params),
                        },
                        {
                            icon: <RemoveRedEyeIcon color="action" />,
                            tooltip: "View",
                            onClick: () => handleView(params),
                        },
                    ]}
                />
            ),
        },
    ];

    const passwordColumns: GridColDef[] = [
        { field: "admin_password", headerName: "Admin Password", flex: 1 },
        { field: "service_engg_password", headerName: "Service Engineer Password", flex: 1.5 },
        { field: "user_password", headerName: "User Password", flex: 1 },
        { field: "created_by", headerName: "Created By", flex: 1 },
        { field: "created_at", headerName: "Created At", flex: 1.2 },
    ];

    useEffect(() => {
        const fetchCompanyMachineInstances = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<CompanyMachineInstance[]> = await getCompanyMachineInstancesByCompanyMachineID(company_machine_id!);
                if (res.status && res.data) {
                    setCompanyMachineInstances(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch machines.");
                }
            } catch (err) {
                errorMessage("Error fetching machines.");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchCompanyMachineInstances();
    }, [company_machine_id]);

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading(prev => ({ ...prev, fetch: true }));
        try {
            const [locationsRes, instancesRes] = await Promise.all([
                getLocationsByCompanyId(company_id || ""),
                getReadyMachineInstancesByMachineID(machine_id),
            ]);

            if (locationsRes.status && locationsRes.data) {
                setLocations(locationsRes.data);
            } else {
                errorMessage(locationsRes.message || "Failed to fetch locations.");
            }

            if (instancesRes.status && instancesRes.data) {
                setInstances(instancesRes.data);
            } else {
                errorMessage(instancesRes.message || "Failed to fetch instances.");
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Error fetching data");
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleAttachPassword = async (params: GridCellParams) => {
        setLoading((prev) => ({ ...prev, fetch: true }));
        setMachineInstanceToAttach(params);

        try {
            const passwordResponse: ApiResponse<GetWindowsAccessPasswordResponse> = await getWindowsAccessPassword({
                machine_instance_id: params.row.machine_instance_id,
                company_machine_id: company_machine_id || "",
            });
            if (passwordResponse.status && passwordResponse.data) {
                setWindowsPasswords(passwordResponse.data);
            }
        } catch (error) {
            console.error("Failed to fetch Windows passwords:", error);
        }
        setPasswordAttachDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: false }));
    };

    const handleView = async (params: GridCellParams) => {
        setLoading((prev) => ({ ...prev, fetch: true }));
        setMachineInstanceToAttach(params);

        try {
            const passwordResponse: ApiResponse<GetAllWindowsAccessPasswordData[]> = await getAllWindowsAccessPasswords({
                machine_instance_id: params.row.machine_instance_id,
                company_machine_id: company_machine_id || "",
            });
            if (passwordResponse.status && passwordResponse.data) {
                setAllWindowsPasswords(passwordResponse.data);
            } else {
                errorMessage(passwordResponse.message || "Failed to fetch fetch Windows passwords data.");
            }
        } catch (error) {
            console.error("Failed to fetch Windows password data:", error);
        }
        setIsViewDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: false }));
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setDialogOpen(false);
    };

    const closeDialogForPasswordAttach = (resetForm: () => void) => {
        resetForm();
        setPasswordAttachDialogOpen(false);
    };

    const confirmDialogForPassword = async (values: any, { resetForm }: { resetForm: () => void }) => {
        await handleCreateWindowsPassword(values);
        closeDialogForPasswordAttach(resetForm);
    };

    const handleCreateWindowsPassword = async (values: any) => {
        setLoading(prev => ({ ...prev, attach: true }));
        try {
            const createdBy = localStorage.getItem("user_id");

            if (!createdBy) {
                errorMessage("User not authenticated.");
                return;
            }
            const passwordParams: CreateWindowsAccessPasswordParams = {
                machine_instance_id: machineInstanceToAttach?.row?.machine_instance_id,
                company_machine_id: company_machine_id!,
                admin_password: values.adminPassword,
                service_engg_password: values.serviceEnggPassword,
                user_password: values.userPassword,
                created_by: createdBy,
            };
            const response = await createWindowsAccessPassword(passwordParams);

            if (response.status) {
                successMessage(response.message || "Windows access password attached successfully.");
            } else {
                errorMessage(response.message || "Failed to attach password.");
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
        } finally {
            setLoading(prev => ({ ...prev, attach: false }));
        }
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        const success = await handleCreate(values);

        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newMachineInstance: CompanyMachineInstanceParams = {
                location_id: values.locationId,
                company_machine_id: company_machine_id!,
                machine_instance_id: values.machineInstanceId,
            };
            const response = await addCompanyMachineInstance(newMachineInstance);
            if (response.status) {
                const location = locations.find(location => location.location_id === values.locationId);
                const machineInstance = {
                    company_machine_id: company_machine_id!,
                    location_id: values.locationId,
                    location_name: location?.location_name || "",
                    machine_instance_id: values.machineInstanceId,
                };
                setCompanyMachineInstances(prev => [...prev, machineInstance]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || "Failed to attach machine.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            const machineInstance: CompanyMachineInstanceParams = {
                location_id: itemToDelete.row.location_id,
                company_machine_id: company_machine_id!,
                machine_instance_id: itemToDelete.row.machine_instance_id,
            };
            const res = await deleteCompanyMachineInstance(machineInstance);
            if (res.status) {
                setCompanyMachineInstances(prev => prev.filter(m => m.machine_instance_id !== itemToDelete.row.machine_instance_id));
                successMessage(res.message);
            } else {
                errorMessage(res.message || "Failed to delete machine.");
            }
        } catch {
            errorMessage("Unexpected error.");
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    const isAllEmpty = Object.values(windowsPasswords).every(value => value === "");

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/company">
                                    Company
                                </Link>
                                <Link color="primary" to={`/company`} state={{ company_name: company_name }}>{company_name}</Link>
                                <Link color="primary" to={`/company/${company_id}/machines`} state={{ company_name: company_name }}>
                                    Machines
                                </Link>
                                <Link color="primary" to={`/company/${company_id}/machines`} state={{ company_name: company_name }}>{machine_name}</Link>
                                <Link color="primary" to={`/company/${company_id}/machines/${company_machine_id}`} state={{ company_name: company_name, machine_name: machine_name }}>Instances</Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                            <GridTable
                                rows={companyMachineInstances.map((row) => ({
                                    ...row,
                                    id: row.machine_instance_id,
                                }))}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.id}
                            />
                        </div>
                        <Formik
                            initialValues={{
                                locationId: "",
                                machineInstanceId: ""
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={"Add Company Machine Instance"}
                                    confirmButtonText={"Add"}
                                    cancelButtonText="Cancel"
                                    icon={<PrecisionManufacturingIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={6}>
                                                            <Dropdown
                                                                options={locations.map((location) => ({
                                                                    id: location.location_id,
                                                                    name: location.location_name,
                                                                }))}
                                                                value={values.locationId ?? ""}
                                                                onChange={(id) => setFieldValue("locationId", id)}
                                                                label="Location"
                                                                required
                                                                error={Boolean(touched.locationId && errors.locationId)}
                                                                helperText={
                                                                    touched.locationId && errors.locationId
                                                                        ? String(errors.locationId)
                                                                        : undefined
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={6}>
                                                            <Dropdown
                                                                options={instances.map((instance) => String(instance))}
                                                                value={values.machineInstanceId ?? ""}
                                                                onChange={(id) => setFieldValue("machineInstanceId", id)}
                                                                label="Machine Instance"
                                                                required
                                                                error={Boolean(touched.machineInstanceId && errors.machineInstanceId)}
                                                                helperText={
                                                                    touched.machineInstanceId && errors.machineInstanceId
                                                                        ? String(errors.machineInstanceId)
                                                                        : undefined
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                        <Formik
                            initialValues={{
                                adminPassword: windowsPasswords ? windowsPasswords.admin_password : "",
                                serviceEnggPassword: windowsPasswords ? windowsPasswords.service_engg_password : "",
                                userPassword: windowsPasswords ? windowsPasswords.user_password : "",
                            }}
                            validationSchema={validationSchemaForWindowsPassword}
                            onSubmit={confirmDialogForPassword}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isPasswordAttachDialogOpen}
                                    onClose={() => closeDialogForPasswordAttach(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={`${isAllEmpty ? "Add Password" : "Update Password"}`}
                                    confirmButtonText={isAllEmpty ? "Add" : "Update"}
                                    cancelButtonText="Cancel"
                                    icon={<LockResetIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={4}>
                                                            <TextField
                                                                id="adminPassword"
                                                                name="adminPassword"
                                                                label="Admin Password"
                                                                required
                                                                value={values.adminPassword}
                                                                onChange={handleChange}
                                                                error={touched.adminPassword && Boolean(errors.adminPassword)}
                                                                helperText={touched.adminPassword && errors.adminPassword}
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                id="serviceEnggPassword"
                                                                name="serviceEnggPassword"
                                                                label="Service Engineer Password"
                                                                required
                                                                value={values.serviceEnggPassword}
                                                                onChange={handleChange}
                                                                error={touched.serviceEnggPassword && Boolean(errors.serviceEnggPassword)}
                                                                helperText={touched.serviceEnggPassword && errors.serviceEnggPassword}
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                id="userPassword"
                                                                name="userPassword"
                                                                label="User Password"
                                                                required
                                                                value={values.userPassword}
                                                                onChange={handleChange}
                                                                error={touched.userPassword && Boolean(errors.userPassword)}
                                                                helperText={touched.userPassword && errors.userPassword}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                        <Dialog
                            open={isViewhDialogOpen}
                            onClose={() => setIsViewDialogOpen(false)}
                            title="Windows Password History"
                            cancelButtonText="Cancel"
                            icon={<LockResetIcon />}
                            message={
                                <Box padding={4}>
                                    {loading.fetch ? (
                                        <Loader color="primary" size={40} />
                                    ) : (
                                        <Box sx={{ width: '100%', minWidth: 800 }}>
                                            <GridTable
                                                rows={allWindowsPasswords.map((row, index) => ({ ...row, id: index }))}
                                                columns={passwordColumns}
                                                pageSize={10}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Company Machine Instance" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this company machine instance?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Instance;
